/**
 * La red de seguridad: convierte una pantalla en blanco en algo que se entiende.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO ES DE LAS COSAS MÁS IMPORTANTES DE LA APLICACIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * En React, si un componente lanza un error mientras se dibuja, React **desmonta
 * el árbol entero**. No enseña nada: deja la página en blanco. Sin un límite de
 * error, un fallo en un rincón —una tarjeta de reportes leyendo un campo que
 * llegó vacío— apaga Softfi completo.
 *
 * Y la persona que lo sufre no es un programador con la consola abierta. Es un
 * cajero con tres clientes en la cola viendo una pantalla negra, sin saber si
 * perdió la venta ni a quién llamar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE DICE, Y POR QUÉ ESO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Tres cosas, en este orden:
 *
 *   1. **"Tus ventas están guardadas"** — primero, porque es la única pregunta
 *      que importa en ese momento. Todo lo cobrado ya está en el servidor o en
 *      la cola de este dispositivo; un fallo dibujando una pantalla no borra
 *      nada.
 *   2. **Un botón de recargar**, que arregla el 95% de los casos.
 *   3. **El detalle técnico, plegado.** Sirve para que nos lo puedan mandar por
 *      WhatsApp, pero enseñarlo de entrada asusta y no ayuda a nadie.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

type Props = { children: ReactNode };
type Estado = { error: Error | null; detalle: string | null };

export class LimiteDeError extends Component<Props, Estado> {
  state: Estado = { error: null, detalle: null };

  static getDerivedStateFromError(error: Error): Partial<Estado> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Va a la consola para poder verlo en desarrollo. Cuando haya un servicio
    // de registro de errores, este es el sitio donde se le manda.
    console.error("Softfi se rompió dibujando:", error, info.componentStack);
    this.setState({ detalle: info.componentStack ?? null });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <PantallaDeError
        error={this.state.error}
        detalle={this.state.detalle}
        onReintentar={() => this.setState({ error: null, detalle: null })}
      />
    );
  }
}

export function PantallaDeError({
  error,
  detalle,
  onReintentar,
}: {
  error: unknown;
  detalle?: string | null;
  onReintentar?: () => void;
}) {
  const mensaje = error instanceof Error ? error.message : String(error);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-[var(--radius-card)] bg-danger-soft text-danger">
          <AlertTriangle size={26} />
        </div>

        <h1 className="text-xl font-semibold text-ink">Algo se rompió en esta pantalla</h1>

        <p className="mt-2 text-sm leading-relaxed text-ink-soft">
          <strong>Tus ventas están guardadas.</strong> Esto es un fallo dibujando la página, no
          en tus datos: nada de lo que cobraste se perdió.
        </p>

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="pulsable inline-flex items-center gap-2 rounded-[var(--radius-control)] bg-accent px-4 py-2.5 text-sm font-semibold text-on-accent hover:bg-accent-hover"
          >
            <RefreshCw size={16} /> Recargar Softfi
          </button>

          {onReintentar && (
            <button
              type="button"
              onClick={onReintentar}
              className="pulsable rounded-[var(--radius-control)] border border-line bg-surface px-4 py-2.5 text-sm font-medium text-ink hover:bg-surface-hover"
            >
              Intentar de nuevo
            </button>
          )}
        </div>

        <details className="mt-6 text-left">
          <summary className="cursor-pointer text-xs text-muted hover:text-ink">
            Detalle técnico (para mandárselo a soporte)
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto rounded-[var(--radius-control)] border border-line bg-surface-2 p-3 text-[11px] leading-relaxed text-muted">
            {mensaje}
            {detalle}
          </pre>
        </details>
      </div>
    </div>
  );
}
